<script lang="ts">
	import Mill from '$lib/logos/mill.svelte';
	import Burger from '$lib/icons/burger.svelte';
	import ShoppingCartIcon from '$lib/icons/shoppingCartIcon.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import BurgerMenu from '$lib/storyblok/components/burgerMenu.svelte';
	import type { SbImage, SbLink } from '$lib/storyblok/types';
	import { fly } from 'svelte/transition';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	interface Props {
		content: ISbStoryData<{
			center_navigation: ISbStoryData['content'][];
			right_navigation: ISbStoryData['content'][];
			cta_title: string;
			cta_link: SbLink;
			nav_cta_label: string;
			nav_cta_link: SbLink;
			burger_image: SbImage;
			alternative_logo?: SbImage;
			transparent_on_mobile?: boolean;
			hide_cart_button?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let burgerMenu = $state<{ open: () => void }>();

	let btnTheme = $state<string | null>(null);

	$effect(() => {
		const hero = document.getElementById('hero');

		if (!hero) return;

		if (hero.classList.contains('hero-bg-light')) {
			btnTheme = 'btn-white';
		} else if (hero.classList.contains('hero-bg-dark')) {
			btnTheme = 'btn-brown';
		} else {
			btnTheme = '';
		}
	});
</script>

<header
	use:editable={content}
	class="absolute inset-x-0 top-0 z-50 flex flex-col transition-transform"
	style="transform: translateY(var(--banner-height)); --mobile-text-color: {content.transparent_on_mobile
		? 'white'
		: 'var(--text-color)'}"
	id="site-header"
>
	{#if btnTheme !== null}
		<div class="flex items-center justify-center px-5 py-3 lg:px-10 lg:py-5" in:fly={{ y: '-33%' }}>
			<a class="pointer-events-auto flex flex-1" title="Home" href="/">
				{#if content.alternative_logo?.filename}
					<Image
						src={content.alternative_logo.filename}
						alt={content.alternative_logo.alt || 'Logo'}
						title={content.alternative_logo.title}
						class={[
							'h-8 lg:h-10',
							{
								'max-lg:hidden': content.transparent_on_mobile
							}
						]}
						height={32}
					/>
				{/if}
				<Mill
					class={[
						'h-8 lg:h-10',
						{
							'text-white lg:text-brown-1': btnTheme === 'btn-white',
							'text-white': btnTheme !== 'btn-white',
							'max-lg:hidden': content.alternative_logo?.filename && !content.transparent_on_mobile,
							'lg:hidden': content.alternative_logo?.filename || content.transparent_on_mobile
						}
					]}
				/>
			</a>

			{#if content.center_navigation && content.center_navigation.length > 0}
				<nav class="flex-[2] max-lg:hidden">
					<ul class="flex items-center justify-center gap-x-2">
						{#each content.center_navigation as item}
							<Blok content={item} btnClasses={btnTheme} />
						{/each}
					</ul>
				</nav>
			{/if}

			<div class="flex flex-1 items-center justify-end gap-x-2 lg:gap-x-[1px]">
				{#if content.right_navigation && content.right_navigation.length > 0}
					<nav class="max-lg:hidden">
						<ul class="flex items-center gap-x-2">
							{#each content.right_navigation as item}
								<Blok content={item} btnClasses={btnTheme} />
							{/each}
						</ul>
					</nav>
				{/if}

				{#if !content.hide_cart_button}
					<Button
						class={['btn-hero-nav btn-tertiary', btnTheme]}
						style="--mobile-text-color: {content.transparent_on_mobile
							? 'white'
							: 'var(--text-color)'}"
						title="Open cart"
						onclick={() => shoppingCart.open()}
					>
						<ShoppingCartIcon />
					</Button>
				{/if}

				{#if content.nav_cta_label}
					<Button href={getSbUrl(content.nav_cta_link)} class="btn-small lg:hidden"
						>{content.nav_cta_label}</Button
					>
				{/if}

				{#if content.right_navigation && content.right_navigation.length > 0}
					<nav class="flex items-center lg:hidden">
						<Button
							class={['btn-hero-nav btn-tertiary', btnTheme]}
							style="--mobile-text-color: {content.transparent_on_mobile
								? 'white'
								: 'var(--text-color)'}"
							onclick={() => burgerMenu?.open()}
							title="Open burger menu"
						>
							<Burger style="width: 40px; height: 40px" />
						</Button>
					</nav>
				{/if}
			</div>
		</div>
	{/if}
</header>

{#if content.right_navigation && content.right_navigation.length > 0}
	<BurgerMenu
		navList={content.center_navigation}
		signIn={content.right_navigation[0]}
		ctaLink={content.cta_link}
		ctaTitle={content.cta_title}
		ctaImage={content.burger_image}
		bind:this={burgerMenu}
	/>
{/if}

<style lang="scss">
	:global(.btn-hero-nav.btn-tertiary) {
		@media screen and (max-width: 1023px) {
			--text-color: var(--mobile-text-color);
		}
	}
</style>
